import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, FileText, Gamepad2, ScrollText } from 'lucide-react';
import api from '../../lib/axios';

// Matrix Rain Effect Component - Casa de Papel Theme
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789€$£¥₿#@&%?!';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    
    const drops = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        const brightness = Math.random();
        if (brightness > 0.95) {
          ctx.fillStyle = '#ffffff';
        } else if (brightness > 0.7) {
          ctx.fillStyle = '#ff3333';
        } else {
          ctx.fillStyle = '#8b0000';
        }
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 50);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0 opacity-15"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

const GameRulesPage = () => {
  const { gameId } = useParams();
  const [rules, setRules] = useState('');
  const [gameName, setGameName] = useState('Le Coffre MSC');
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchRules();
  }, [gameId]);

  const fetchRules = async () => {
    try {
      // Get rules from the annual game (Le Coffre MSC)
      const annualRes = await api.get(`/game/annual-game/${currentYear}/rules`);
      if (annualRes.data?.rules) {
        setRules(annualRes.data.rules);
        setGameName(annualRes.data.game_name || 'Le Coffre MSC');
      } else {
        // Fallback: try to get rules from the specific game
        if (gameId) {
          const gameRes = await api.get(`/game/contests/${gameId}`);
          if (gameRes.data?.rules) {
            setRules(gameRes.data.rules);
            setGameName(gameRes.data.title || 'Jeu Concours');
          }
        }
        
        // If still no rules, show default message
        if (!rules) {
          setRules('Règlement en cours de rédaction...');
        }
      }
    } catch (e) {
      console.error(e);
      setRules('Règlement en cours de rédaction...');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <MatrixRain />
      <div className="fixed inset-0 bg-gradient-to-b from-red-900/20 via-black/30 to-red-900/20 pointer-events-none z-0" />
      
      {/* Fixed Header */}
      <header className="border-b border-red-500/30 bg-black/95 backdrop-blur-md fixed top-0 left-0 right-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link 
            to={gameId ? `/jeu/${gameId}` : '/le-coffre-msc'} 
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-950/80 hover:bg-red-900 transition-colors border border-red-500/30"
          >
            <ArrowLeft className="w-4 h-4 text-red-400" />
            <span className="text-sm text-white">Retour</span>
          </Link>
          <Link 
            to="/mon-espace-joueur" 
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-colors shadow-lg shadow-red-500/30"
          >
            <span className="text-white text-sm font-semibold">Mon Espace Joueur</span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto pt-24 pb-12 px-4 relative z-10">
        <Card className="bg-black border-2 border-red-500/50 shadow-xl shadow-red-500/20">
          <CardHeader className="border-b border-red-500/30">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shadow-lg shadow-red-500/40">
                <ScrollText className="w-7 h-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl text-white">Règlement du jeu concours</CardTitle>
                <p className="text-sm text-red-400 mt-1">{gameName} {currentYear}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-red-500"></div>
              </div>
            ) : (
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-white/80 leading-relaxed text-sm">
                  {rules}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={gameId ? `/jeu/${gameId}` : '/le-coffre-msc'}>
            <Button className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/30">
              <Gamepad2 className="w-4 h-4 mr-2" />
              Participer au jeu
            </Button>
          </Link>
          <Link to="/le-coffre-msc">
            <Button variant="outline" className="w-full sm:w-auto border-red-500/50 text-red-400 hover:bg-red-950/50">
              <FileText className="w-4 h-4 mr-2" />
              Découvrir Le Coffre MSC
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GameRulesPage;
